'use client'

// The protected-state leaf that replaces only a row's normal text preview.
// The registered surface variant still owns the surrounding row, title,
// identity, date, selection state, and layout. A successful challenge runs
// the existing get_locked -> reimport -> projection-sync path, so this leaf
// is naturally replaced by the ordinary text when the row unlocks.

import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from 'react'
import type { SubRenderState } from '@/msgdeskRuntime/state'
import { dispatchSubUnlock } from '@/msgdeskRuntime/subsAccess'
import type { BlockedPresentation, BlockedState } from './blockedPresentation'
import { useRowSurface } from '../rowTemplates/RowSurfaceContext'

const LABELS: Record<Exclude<BlockedState, 'none'>, string> = {
  showpinned: 'Protected',
  'time-early': 'Not open yet',
  'time-elapsed': 'No longer available',
}

function stopRowAction(event: MouseEvent<HTMLElement>) {
  event.stopPropagation()
}

export function BlockedPreview({
  sub,
  presentation,
  hasEditPin,
}: {
  sub: SubRenderState
  presentation: BlockedPresentation
  hasEditPin: boolean
}) {
  const skin = useRowSurface().skin.blockedNotice
  const inputRef = useRef<HTMLInputElement>(null)
  const [editing, setEditing] = useState(false)
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [refused, setRefused] = useState(false)

  const editOnly = presentation.pinKind === 'edit-only'
  const canChallenge = presentation.pinKind === 'show-or-edit' || (editOnly && hasEditPin)

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing, refused])

  if (presentation.blocked === 'none') return null

  const closeEmpty = () => {
    setPin('')
    setRefused(false)
    setEditing(false)
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const value = pin.trim()
    if (!value) {
      closeEmpty()
      return
    }
    if (busy) return

    setBusy(true)
    setRefused(false)
    void dispatchSubUnlock({ elId: sub.elId, pin: value, isTimeLocked: editOnly }).then((unlocked) => {
      setBusy(false)
      if (!unlocked) {
        setRefused(true)
        setPin('')
      }
    })
  }

  if (!editing || !canChallenge) {
    const contents = (
      <>
        <span className={`md-sub-blocked-preview__icon ${skin.icon}`} aria-hidden="true">🔒</span>
        <span className="md-sub-blocked-preview__label">{LABELS[presentation.blocked]}</span>
      </>
    )

    return (
      <span className={`md-sub-blocked-preview md-sub-blocked-preview--${presentation.blocked} ${skin.root}`}>
        {canChallenge ? (
          <button
            type="button"
            className={`md-sub-blocked-preview__badge ${skin.badge} ${skin.badgeButton}`}
            title={presentation.text}
            aria-label={`${presentation.text} Enter password`}
            onClick={(event) => {
              stopRowAction(event)
              setEditing(true)
            }}
          >
            {contents}
          </button>
        ) : (
          <span
            className={`md-sub-blocked-preview__badge ${skin.badge} ${skin.badgeStatic}`}
            title={presentation.text}
          >
            {contents}
          </span>
        )}
      </span>
    )
  }

  return (
    <span className={`md-sub-blocked-preview md-sub-blocked-preview--${presentation.blocked} ${skin.root}`}>
      <form className={`md-sub-blocked-preview__form ${skin.form}`} onSubmit={submit} onClick={stopRowAction}>
        <input
          ref={inputRef}
          type="password"
          className={`md-sub-blocked-preview__input ${skin.input}`}
          value={pin}
          onChange={(event) => {
            setPin(event.target.value)
            if (refused) setRefused(false)
          }}
          onBlur={(event) => {
            if (!event.currentTarget.value.trim()) closeEmpty()
          }}
          placeholder={editOnly ? 'Edit PIN' : 'PIN'}
          aria-label={editOnly ? 'Edit PIN' : 'Show or edit PIN'}
          autoComplete="off"
          disabled={busy}
        />
        <button type="submit" className={`md-sub-blocked-preview__button ${skin.button}`} disabled={busy}>
          {busy ? '…' : 'Open'}
        </button>
        {refused ? (
          <span className={`md-sub-blocked-preview__error ${skin.error}`} role="alert">Wrong PIN</span>
        ) : null}
      </form>
    </span>
  )
}
